import React from "react";
import { useNavigate, useLocation } from "react-router";
import {
  BottomNavigation,
  BottomNavigationAction,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import "./BottomNavigationMenu.scss";

interface BottomNavigationMenuProps {
  menuData: {
    iconName: string;
    key: string;
    title: string;
  }[];
  moreMenuDataEnabled: boolean;
  moreMenuData: {
    iconName: string;
    key: string;
    title: string;
  }[];
  onNodeClick?: (e: any, key: string) => void;
}

const BottomNavigationMenu: React.FC<BottomNavigationMenuProps> = ({
  menuData,
  moreMenuDataEnabled,
  moreMenuData,
  onNodeClick: onNodeClickProp,
}) => {
  const [value, setValue] = React.useState<number>(1);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const bottomMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const pathNameArray = pathName.split("/");

  const onNodeClick = (e: any, key: any) => {
    if (onNodeClickProp) {
      onNodeClickProp(e, key);
      return;
    }
    navigate(key);
  };

  const onBottomNavigationClick = (event: any, newValue: number) => {
    setValue(newValue);
    const isMoreIndex = moreMenuDataEnabled && newValue === menuData.length;
    if (isMoreIndex) {
      setAnchorEl(event.currentTarget);
      return;
    }
    const item = menuData[newValue];
    if (!item) return;
    onNodeClick(null, item.key);
  };

  const handleBottomMenuClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    // set current tab based on pathname
    const currentKey = pathNameArray[pathNameArray.length - 1];
    const currentIndex = menuData.findIndex((m) => m.key === currentKey);
    if (currentIndex >= 0) setValue(currentIndex);
  }, [location.pathname, menuData]);

  return (
    <>
      <BottomNavigation
        showLabels
        value={value}
        onChange={(event, newValue) => {
          onBottomNavigationClick(event, newValue);
        }}
      >
        {menuData.map((item) => (
          <BottomNavigationAction
            key={item.key}
            label={item.title}
            icon={
              <Iconography
                icon={item.iconName as any}
                size={IconographySizes.Medium}
              />
            }
          />
        ))}
        {moreMenuDataEnabled && moreMenuData.length > 0 && (
          <BottomNavigationAction
            label="More"
            icon={
              <Iconography
                icon="circleEllipsis"
                size={IconographySizes.Medium}
              />
            }
            aria-controls={bottomMenuOpen ? "bottom-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={bottomMenuOpen ? "true" : undefined}
          />
        )}
      </BottomNavigation>
      <Menu
        id="bottom-menu"
        anchorEl={anchorEl}
        open={bottomMenuOpen}
        onClose={handleBottomMenuClose}
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        transformOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {moreMenuData.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => onNodeClick(null, item.key)}
            sx={{
              color:
                pathNameArray[pathNameArray.length - 1] === item.key
                  ? "#003087"
                  : "#222222",
            }}
          >
            <ListItemIcon>
              <Iconography
                icon={item.iconName}
                size={IconographySizes.Medium}
                className={
                  pathNameArray[pathNameArray.length - 1] === item.key
                    ? "mui-bottom-navigation-selected-icon"
                    : ""
                }
              />
            </ListItemIcon>
            <ListItemText>{item.title}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default BottomNavigationMenu;
